import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { Modal } from "@/components/design/Modal";
import { getConfig } from "@/lib/config";
import type { PreviewData } from "./profile-merge-types";
import { useProfileMergeStore } from "@/stores/profile-merge-store";

// LinkedIn DMA import dialog. The actual OAuth + genesis import runs in the
// BFF (see apps/bff .../routes/auth/linkedin.ts): this dialog only kicks off
// the flow (full-page navigation to /auth/linkedin/start) and, when the user
// returns to /save?linkedin=<marker>, reads the one-shot outcome from
// /auth/linkedin/outcome. The LinkedIn token never reaches the browser — only
// this small outcome does.
//
// There is NO in-dialog preview/review step. Once the background import produces
// a merge preview, the dialog publishes it to the merge store and sends the user
// straight to the profile page, where the changes are reviewed IN-LINE per field
// (see ProfileMergeField). The dialog itself only covers connect → wait → hand-off.

interface LinkedInOutcome {
  ok: boolean;
  // True while the background import is still running on the BFF; the dialog keeps
  // polling /auth/linkedin/outcome until this is absent/false.
  pending?: boolean;
  errorCode?: string;
  // Non-destructive merge preview (a stateless per-field/per-row diff) produced
  // by the background previewLinkedInImport. Present (with ok === true) once the
  // preview is ready — the dialog then hands it to the in-line review.
  preview?: PreviewData;
  warnings?: string[];
}

// The import runs in the background on the BFF; the dialog polls the outcome
// endpoint every POLL_INTERVAL_MS until it is non-pending, giving up after
// POLL_MAX_MS (the import keeps running server-side and is idempotent, so a
// later page refresh still surfaces the result).
const POLL_INTERVAL_MS = 2_000;
const POLL_MAX_MS = 120_000;

// While importing, a seconds counter shows how long the user has been waiting
// (counted from the OAuth return — consent time on linkedin.com is the user's,
// not ours). After ABORT_TIP_AFTER_S a hint suggests aborting and starting the
// import over: the preview writes nothing, so a fresh attempt is always safe.
const ELAPSED_TICK_MS = 1_000;
const ABORT_TIP_AFTER_S = 20;

// Outcome code the BFF stores when genesis reports that LinkedIn has not built the
// member's DMA snapshot yet (first import right after consent: LinkedIn answers 404
// for every domain until the export is prepared). Nothing failed and nothing the
// user can fix, only wait out: the dialog explains it and offers a retry that stays
// locked for RETRY_COOLDOWN_S, because LinkedIn needs minutes rather than seconds
// and an instant retry would only reproduce the same empty snapshot.
const SNAPSHOT_NOT_READY_CODE = "linkedin.error.snapshotNotReady";
const RETRY_COOLDOWN_S = 60;

// The page the in-line merge review lives on (profile basics). On a ready
// preview the dialog navigates here so the incoming values show under the fields.
const REVIEW_PATH = "/save/basics";

// Stable no-op so the result Modal can be made non-dismissible while the import
// is still running (a stray backdrop/ESC click must not discard the in-flight
// result). A stable reference avoids re-running the Modal's ESC-listener effect.
const noop = (): void => {};

function authPath(endpoint: "start" | "outcome"): string {
  const base = getConfig().basePath;
  const prefix = base.endsWith("/") ? base : `${base}/`;
  return `${prefix}auth/linkedin/${endpoint}`;
}

// m:ss — the wait is bounded by POLL_MAX_MS (2 min), so no hour segment.
function formatElapsed(totalSeconds: number): string {
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`;
}

// True when a preview carries at least one actionable (non-UNCHANGED) item across
// any section — i.e. there is something to review in-line. An all-UNCHANGED (or
// empty) preview means the profile already has everything LinkedIn could add.
function previewHasChanges(preview: PreviewData): boolean {
  const rows = [
    ...preview.experiences,
    ...preview.educations,
    ...preview.languageSkills,
    ...preview.certificates,
    ...preview.expertises,
  ];
  return (
    preview.personDetail.some((f) => f.action !== "UNCHANGED") ||
    rows.some((r) => r.action !== "UNCHANGED")
  );
}

// When the outcome endpoint has nothing (e.g. not_configured never stores one,
// or it was already consumed), fall back to a sensible outcome from the marker.
function markerFallback(marker: string): LinkedInOutcome {
  switch (marker) {
    case "ok":
      return { ok: true };
    case "denied":
      return { ok: false, errorCode: "linkedin.error.authorize_denied" };
    case "not_configured":
      return { ok: false, errorCode: "linkedin.error.not_configured" };
    default:
      return { ok: false, errorCode: "linkedin.error.import_failed" };
  }
}

export function LinkedInImportDialog({
  returnMarker,
  onClose,
}: {
  // Non-null when the dialog opened because the OAuth roundtrip returned us to
  // /save?linkedin=<marker>. null = opened fresh from the service card (intro).
  returnMarker: string | null;
  onClose: () => void;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(returnMarker !== null);
  const [outcome, setOutcome] = useState<LinkedInOutcome | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  // Latest-ref pattern for onClose so the hand-off effect doesn't depend on the
  // parent's fresh-each-render arrow (which would re-run it).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Hand-off: once the merge preview is ready, publish it to the store and send
  // the user to the in-line review on the profile page — no in-dialog preview.
  useEffect(() => {
    if (!outcome?.preview || !previewHasChanges(outcome.preview)) return;
    useProfileMergeStore.getState().start(outcome.preview);
    navigate(REVIEW_PATH);
    onCloseRef.current();
  }, [outcome?.preview, navigate]);

  // Elapsed wait counter, ticking while the background import runs. Computed
  // from a start timestamp rather than incremented so a throttled background
  // tab can't drift the display; performance.now() (not Date.now()) because it
  // is monotonic — an OS clock step during the wait must not render a negative
  // time or fire the abort tip early.
  const importInProgress = returnMarker === "pending" && loading;
  useEffect(() => {
    if (!importInProgress) return;
    const startedAt = performance.now();
    const id = setInterval(() => {
      setElapsedSeconds(Math.floor((performance.now() - startedAt) / 1000));
    }, ELAPSED_TICK_MS);
    return () => clearInterval(id);
  }, [importInProgress]);

  // Retry cooldown for the snapshot-not-ready outcome, same monotonic-clock
  // pattern as the elapsed counter. Counts down from RETRY_COOLDOWN_S once that
  // outcome lands; the retry only turns into an active link at zero.
  const snapshotNotReady = !loading && outcome?.ok === false && outcome.errorCode === SNAPSHOT_NOT_READY_CODE;
  const [retryCooldownS, setRetryCooldownS] = useState(RETRY_COOLDOWN_S);
  useEffect(() => {
    if (!snapshotNotReady) return;
    const startedAt = performance.now();
    const id = setInterval(() => {
      const remaining = RETRY_COOLDOWN_S - Math.floor((performance.now() - startedAt) / 1000);
      setRetryCooldownS(Math.max(0, remaining));
      if (remaining <= 0) clearInterval(id);
    }, ELAPSED_TICK_MS);
    return () => clearInterval(id);
  }, [snapshotNotReady]);

  // The import runs in the BACKGROUND on the BFF, so /auth/linkedin/outcome
  // returns a `pending` outcome until the detached genesis import writes the
  // terminal result. We POLL it (peek; the server does NOT clear, so overlapping
  // reads and React StrictMode's double-invoke are harmless) until it is
  // non-pending or POLL_MAX_MS elapses. The cancel guard stops the loop when the
  // dialog unmounts so a closed dialog never keeps polling.
  useEffect(() => {
    if (returnMarker === null) return;
    let cancelled = false;
    const deadline = Date.now() + POLL_MAX_MS;
    const finish = (got: LinkedInOutcome) => {
      /* v8 ignore next -- defensive: every finish() callsite is reached only after an identical `if (cancelled) return` with no intervening await, so `cancelled` is always false here; the guard can never fire */
      if (cancelled) return;
      setOutcome(got);
      setLoading(false);
    };
    void (async () => {
      while (!cancelled) {
        let got: LinkedInOutcome | null;
        try {
          const res = await fetch(authPath("outcome"), {
            credentials: "include",
            headers: { Accept: "application/json" },
            signal: AbortSignal.timeout(10_000),
          });
          const body = (await res.json()) as { outcome: LinkedInOutcome | null };
          got = body.outcome;
        } catch {
          got = null;
        }
        if (cancelled) return;
        // Terminal outcome from the server → render it.
        if (got && !got.pending) return finish(got);
        // No stored outcome: for a terminal marker (denied/error/not_configured)
        // there is nothing to wait for → use the marker fallback. For "pending"
        // (the success path) a missing outcome just means the store hasn't caught
        // up yet → keep polling.
        if (got === null && returnMarker !== "pending") {
          return finish(markerFallback(returnMarker));
        }
        // Still importing. Give up after the deadline so the dialog never hangs —
        // the import keeps running server-side and is idempotent, so a later
        // refresh surfaces the result.
        if (Date.now() >= deadline) {
          return finish({ ok: false, errorCode: "linkedin.error.stillImporting" });
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [returnMarker]);

  // Full-page navigation target: SPA → BFF → LinkedIn consent → back to
  // /save?linkedin=<marker>. Rendered as a real link (not a button + JS
  // navigation) per the design guideline, styled as a primary button via
  // buttonClasses(). `next` brings the user back to this page.
  const startHref = `${authPath("start")}?next=${encodeURIComponent(window.location.pathname)}`;

  const title = intl.formatMessage({ id: "linkedin.import.dialogTitle", defaultMessage: "LinkedIn import" });

  // Intro phase — opened fresh from the card.
  if (returnMarker === null) {
    return (
      <Modal
        open
        onClose={onClose}
        title={title}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
            <a href={startHref} className={buttonClasses({ variant: "primary" })}>
              <FormattedMessage id="linkedin.import.connect" defaultMessage="Connect LinkedIn" />
            </a>
          </>
        }
      >
        <p className="field-meta leading-snug text-ink/80">
          <FormattedMessage
            id="linkedin.import.intro"
            defaultMessage="Connect your LinkedIn account to import your profile, positions and skills. You'll review what to keep or replace on your profile before anything is saved."
          />
        </p>
      </Modal>
    );
  }

  // Return phase — importing (pending), finishing, error, or handing off to the
  // in-line review. While the import is still running the dialog is
  // NON-DISMISSIBLE: a stray backdrop/ESC click must not throw away the in-flight
  // result. The user either waits or explicitly aborts via the footer button.
  // (Aborting only stops the SPA from waiting — genesis already started the
  // import and has no cancel, so it still completes server-side; it's idempotent.)
  return (
    <Modal
      open
      onClose={importInProgress ? noop : onClose}
      title={title}
      footer={
        snapshotNotReady ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              <FormattedMessage id="linkedin.import.comeBackLater" defaultMessage="Come back later" />
            </Button>
            {retryCooldownS > 0 ? (
              <Button variant="primary" disabled className="tabular-nums">
                <FormattedMessage
                  id="linkedin.import.retryIn"
                  defaultMessage="Try again ({time})"
                  values={{ time: formatElapsed(retryCooldownS) }}
                />
              </Button>
            ) : (
              // Real link per the design guideline, like Connect: the retry re-enters
              // the OAuth flow. Consent already exists, so LinkedIn bounces straight
              // back and the BFF runs a fresh preview against the (hopefully) built snapshot.
              <a href={startHref} className={buttonClasses({ variant: "primary" })}>
                <FormattedMessage id="linkedin.import.retry" defaultMessage="Try again" />
              </a>
            )}
          </>
        ) : (
          <Button
            variant={importInProgress ? "secondary" : "primary"}
            onClick={onClose}
            // Abort sits bottom-LEFT (mr-auto pushes it left within the footer's
            // justify-end row).
            className={importInProgress ? "mr-auto" : undefined}
          >
            {importInProgress ? (
              <FormattedMessage id="linkedin.import.abort" defaultMessage="Abort import" />
            ) : (
              <FormattedMessage id="linkedin.import.close" defaultMessage="Close" />
            )}
          </Button>
        )
      }
    >
      {/* Live region: the loading→result swap happens after an async fetch, so
          screen readers need it announced (polite). */}
      <div role="status" aria-live="polite">
        {loading ? (
          <p className="field-meta leading-snug text-ink/80">
            {returnMarker === "pending" ? (
              <FormattedMessage
                id="linkedin.import.importing"
                defaultMessage="Importing your LinkedIn data – this can take up to a minute…"
              />
            ) : (
              <FormattedMessage id="linkedin.import.loadingResult" defaultMessage="Finishing import…" />
            )}
          </p>
        ) : outcome && !outcome.ok ? (
          <p className="field-meta leading-snug text-ink/80">
            {intl.formatMessage({
              id: outcome.errorCode ?? "linkedin.error.unexpected",
              defaultMessage: "Something went wrong during the import. Please try again later.",
            })}
          </p>
        ) : outcome?.preview && previewHasChanges(outcome.preview) ? (
          // Transient: the hand-off effect navigates to the in-line review and
          // closes the dialog as soon as the preview lands.
          <p className="field-meta leading-snug text-ink/80">
            <FormattedMessage
              id="linkedin.import.openingReview"
              defaultMessage="Opening your profile to review the changes…"
            />
          </p>
        ) : (
          <p className="field-meta leading-snug text-ink/80">
            <FormattedMessage
              id="linkedin.import.alreadyUpToDate"
              defaultMessage="Everything LinkedIn could add is already in your profile."
            />
          </p>
        )}
      </div>
      {importInProgress && (
        <>
          {/* Deliberately aria-hidden and OUTSIDE the live regions: a
              per-second tick in a polite region would be announced on every
              change and spam screen readers. */}
          <p className="field-meta mt-2 tabular-nums text-ink/40" aria-hidden="true">
            <FormattedMessage
              id="linkedin.import.elapsed"
              defaultMessage="Elapsed time: {time}"
              values={{ time: formatElapsed(elapsedSeconds) }}
            />
          </p>
          {/* Mounted (empty) for the whole importing phase: live regions only
              announce changes INSIDE an already-present region, so the tip's
              text toggles within it. */}
          <p role="status" aria-live="polite" className="field-meta mt-2 leading-snug text-ink/80">
            {elapsedSeconds >= ABORT_TIP_AFTER_S && (
              <FormattedMessage
                id="linkedin.import.abortTip"
                defaultMessage="Still importing – you can keep waiting, or abort and start the import over from the beginning. Nothing has been saved yet."
              />
            )}
          </p>
        </>
      )}
    </Modal>
  );
}
